import { Organizer, OrganizerType } from "../../../shared/types/OrganizerTypes"
import { getOrderPerson } from "../../../shared/utils/getOrderPerson"
import { OutlookMeetingValue, OutlookSessionType, SessionValue } from "./outlookCreateOrderTypes"

export const isOutlookMeetingValue = (v: SessionValue): v is OutlookMeetingValue =>
    v.type === OutlookSessionType.OUTLOOK_MEETING

/**
 * An `ORGANIZER` that is a *mailbox* rather than a person.
 *
 * On an order from a shared calendar, `ORGANIZER` is reserved for the calendar the meeting lives in
 * and the person travels as `CREATOR`. Recognised by "has an email, has no user id": the API returns
 * `id: 0` (not a missing field) for organizers that reference no user, and a free-text responsible is
 * id-less too but never carries an email — so requiring one keeps those recognised as people.
 *
 * Only meaningful on a *saved* order: there the user's own calendar rides on their own ORGANIZER
 * entry, which has an id, so this is false for it. It says nothing on the create path — the session
 * the add-in builds has no ids at all, so its ORGANIZER is id-less with an address for every
 * calendar. Use `isSharedCalendarOrder` when you need to know which kind of calendar it is.
 */
export function isCalendarOrganizer(organizer: Organizer): boolean {
    return (organizer.type ?? organizer.organizerType) === OrganizerType.ORGANIZER && !organizer.id && !!organizer.email
}

/**
 * Whether the meeting lives in a calendar shared with other people, which decides the organizer
 * layout: only a shared calendar needs an ORGANIZER of its own, taking the one id-less slot the API
 * tolerates away from the meeting responsible.
 *
 * `flag` is what the add-in reported. It is `undefined` when nobody reported anything — an add-in
 * older than the flag, or an order edited from Manager outside the Outlook flow — and only then is
 * the saved shape a usable fallback (see `isCalendarOrganizer`). Never derive this from the presence
 * of `ORGANIZER.email`: the add-in sends that address for every calendar.
 */
export function isSharedCalendarOrder(flag: boolean | undefined, calendarOrganizer?: Organizer): boolean {
    if (flag !== undefined) return flag

    return !!calendarOrganizer && isCalendarOrganizer(calendarOrganizer)
}

/**
 * The `ORGANIZER` carrying the Outlook calendar address, whether or not it also references a user.
 *
 * The address is what the API authorizes workgroup members by and only the add-in can read it, so
 * every save has to pass it on unchanged — dropping it on an edit would revoke the workgroup's
 * access. Deliberately wider than `isCalendarOrganizer`: on the user's own calendar the address
 * rides along with their own id, and it still must survive.
 */
export function findOrganizerAddress(organizers?: Organizer[]): Organizer | undefined {
    return organizers?.find(
        (organizer) => (organizer.type ?? organizer.organizerType) === OrganizerType.ORGANIZER && !!organizer.email,
    )
}

/**
 * The calendar entry as a request payload. The API echoes it back with `id: 0` and both
 * discriminators; neither belongs in a write, and re-sending `id: 0` is the "unlinked label"
 * hazard. The caller adds the discriminator its endpoint expects.
 */
export function toCalendarOrganizerPayload(organizer: Organizer): Pick<Organizer, "name" | "email"> {
    return { name: organizer.name, email: organizer.email }
}

/**
 * Who to seed the "meeting responsible" field with.
 *
 * `getOrderPerson` ranks ORGANIZER above CREATOR — a contract shared with the Outlook add-in and
 * deliberately left alone. That ranking assumes ORGANIZER is a person, which stops being true once
 * it carries the calendar: `person` then resolves to a mailbox address. Reachable both before an
 * Outlook order is saved (its only organizers come from the session) and after, on an order that
 * ended up without a RESPONSIBLE.
 */
export function getResponsibleSeed(organizers?: Organizer[]): Organizer | undefined {
    const { person, creator } = getOrderPerson(organizers)

    return person && isCalendarOrganizer(person) ? creator : person
}
